// documents.component.ts - Simplified v2 fallback
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { TruncatePipe } from 'src/app/pipe/truncate.pipe';
import { documentServices } from 'src/app/services/document.service';
import { environment } from 'src/environments/environment';
import { ImageSectionComponent } from './show-file';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RecaptchaV3Service } from 'src/app/services/recaptcha-v3.service';
import { RecaptchaService } from 'src/app/services/recaptcha.service';

@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [
    CommonModule, 
    MatIcon, 
    TruncatePipe, 
    ImageSectionComponent, 
    MatButtonModule,
    FormsModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss'
})
export class DocumentsComponent implements OnInit, OnDestroy {
  draftOrNotSubmittedDocuments: any[] = [];
  submittedDocuments: any[] = [];
  apiUrl = environment.apiUrl;
  toggleStates: { [key: number]: boolean } = {};
  selectedFiles: { [key: number]: File | null } = {};
  uploadingStates: { [key: number]: boolean } = {};
  token: string;
  lead_id: any;
  showErrorMessage: any;
  logoUrl: string = '';
  isPublicRoute: boolean = false;

  constructor(
    private documentService: documentServices, 
    private route: ActivatedRoute,
    private recaptchaV3Service: RecaptchaV3Service,
    private recaptchaService: RecaptchaService,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit(): Promise<void> {
    // Check if this is a public route (has token parameter)
    this.token = this.route.snapshot.queryParamMap.get('token');
    this.lead_id = this.route.snapshot.paramMap.get('lead_id');
    this.isPublicRoute = !!this.token;

    // Pre-load reCAPTCHA for public routes only
    if (this.isPublicRoute) {
      try {
        await this.recaptchaV3Service.execute('page_load');
      } catch (error) {
        console.error('Failed to initialize reCAPTCHA:', error);
      }
    }
    
    this.loadDocuments();
  }

  ngOnDestroy(): void {
    // Clean up if needed
  }

  async loadDocuments() {
    try {
      let queryParam: any = {}
      if (this.token) {
        queryParam.token = this.token
        queryParam.id = this.lead_id
      }
      const response: any = await firstValueFrom(this.documentService.generateDocuments(queryParam));
      const companyLogo = response.logoUrl
      this.logoUrl = companyLogo ? `${environment.publicCloudFlareUrl}/company-logo/thumb_${companyLogo}` : '';
      this.splitDocumentsByStatus(response.data);
    } catch (error) {
      this.showErrorMessage = error?.error?.message
    }
  }

  splitDocumentsByStatus(documents: any[]): void {
    this.draftOrNotSubmittedDocuments = documents.filter(doc =>
      doc.status === 'DRAFT' || doc.status === 'NOT_SUBMITTED' || doc.status === 'REJECTED'
    );

    this.submittedDocuments = documents.filter(doc =>
      doc.status === 'SUBMITTED' || doc.status === 'APPROVED'
    );
  }

  toggleUpload(documentId: number): void {
    this.toggleStates[documentId] = !this.toggleStates[documentId];
  }

  onFileSelected(event: any, documentId: number): void {
    const file: File = event.target.files[0];
    if (file) {
      this.selectedFiles[documentId] = file;
    }
  }

  removeFile(documentId: number, fileInput: HTMLInputElement): void {
    this.selectedFiles[documentId] = null;
    fileInput.value = '';
  }

  async uploadDocument(document: any): Promise<void> {
    const file = this.selectedFiles[document.id];
    if (!file) {
      console.error('No file selected');
      return;
    }

    // Set uploading state
    this.uploadingStates[document.id] = true;

    try {
      // Try to upload with v3 first
      await this.uploadWithRecaptcha(document, file, 'v3');
    } catch (error: any) {
      console.error('v3 upload failed:', error);
      
      // If v3 failed, try v2
      if (this.isPublicRoute) {
        this.snackBar.open('Additional verification required', 'OK', {
          duration: 2000
        });

        try {
          const v2Token = await this.recaptchaService.showCaptchaModal({
            autoClose: true,
            autoCloseDelay: 500
          });

          if (v2Token) {
            await this.uploadWithRecaptcha(document, file, 'v2', v2Token);
          } else {
            this.snackBar.open('Verification cancelled', 'OK', {
              duration: 3000,
              panelClass: 'error-snackbar'
            });
          }
        } catch (v2Error) {
          console.error('v2 upload also failed:', v2Error);
          this.snackBar.open('Upload failed. Please try again.', 'OK', {
            duration: 3000,
            panelClass: 'error-snackbar'
          });
        }
      } else {
        this.snackBar.open('Upload failed. Please try again.', 'OK', {
          duration: 3000,
          panelClass: 'error-snackbar'
        });
      }
    } finally {
      this.uploadingStates[document.id] = false;
      this.toggleStates[document.id] = false;
      this.selectedFiles[document.id] = null;
    }
  }

  private async uploadWithRecaptcha(document: any, file: File, version: 'v3' | 'v2', providedToken?: string): Promise<void> {
    let captchaToken: string | null = null;

    // Get captcha token
    if (this.isPublicRoute) {
      if (version === 'v3') {
        captchaToken = await this.recaptchaV3Service.execute('upload_document');
      } else {
        captchaToken = providedToken || null;
      }

      if (!captchaToken) {
        throw new Error('No captcha token available');
      }
    }

    // Prepare upload
    const formData = new FormData();
    let obj: any = { 
      id: document.id, 
      preValue: document.status 
    };
    
    if (this.token) {
      obj.token = this.token;
      obj.lead_id = this.lead_id;
      
      if (captchaToken) {
        obj.recaptchaToken = captchaToken;
        obj.recaptchaAction = 'upload_document';
        obj.recaptchaVersion = version;
      }
    }

    formData.append('file', file);
    formData.append('data', JSON.stringify(obj));

    const { response: uploadResponse }: any = await firstValueFrom(
      this.documentService.postDocument(formData, this.token)
    );

    // Success - Update UI
    this.draftOrNotSubmittedDocuments = this.draftOrNotSubmittedDocuments.filter(
      doc => doc.id !== document.id
    );
    this.submittedDocuments.push(uploadResponse);
    
    this.snackBar.open('Document uploaded successfully!', 'OK', {
      duration: 3000,
      panelClass: 'success-snackbar'
    });
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'insert_drive_file';
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (extension === 'pdf') return 'picture_as_pdf';
    if (['xls', 'xlsx'].includes(extension)) return 'table_chart';
    if (['doc', 'docx'].includes(extension)) return 'description';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return 'image';
    
    return 'insert_drive_file';
  }

  getStatusIcon(status: string): string {
    switch (status.toUpperCase()) {
      case 'SUBMITTED': return 'hourglass_empty';
      case 'APPROVED': return 'check_circle';
      default: return 'info';
    }
  }
}